import { Inject, Injectable, NgZone } from '@angular/core';
import {
  AuditAction,
  EmployeeEditRecord,
  IEmployeeEventData,
  IPayrollEventData,
  ITaskEventData
} from '../models/audit-action';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { PagedPayload } from '../models/paged-payload';
import { isFormatted, parseDatePickerValue } from '../utils/datepicker-value-parser';
import { PayrollJournalEntry, PayrollJournalSearchConfig } from '../models/payroll-journal';
import { OAuthService } from 'angular-oauth2-oidc';
import { PayTypes } from '../models/payroll';

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  constructor(
    private http: HttpClient,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any,
    @Inject('loadingInterceptorSkipHeader') private loadingInterceptorSkipHeader: any,
    @Inject('env') private env: any
  ) {}

  public audit(activity: string, data?: object) {
    let headers = new HttpHeaders();
    headers = headers.set(this.errorInterceptorSkipHeader, '');
    headers = headers.set(this.loadingInterceptorSkipHeader, '');

    // subscribe() and don't return an observable because we just want to issue the call
    // and forget about it; we're never doing anything w the response
    this.http
      .post<any>(
        `${this.env.apiUrlRoot}/Audit`,
        {
          Activity: activity,
          Data: data
        },
        {
          headers
        }
      )
      .subscribe();
  }

  public auditPayroll(activity: string, data: IPayrollEventData, extras?: any) {
    const shapedData = this.shapePayrollData(data, extras);

    return this.audit(activity, shapedData);
  }

  public auditEmployee(activity: string, data: IEmployeeEventData, extras?: any) {
    return this.audit(activity, { ...data, ...extras });
  }

  public auditTask(activity: string, data: ITaskEventData, extras?: any) {
    return this.audit(activity, { ...data, ...extras });
  }

  public auditPayrollWithResponse(activity: string, data: IPayrollEventData, extras?: any) {
    const shapedData = this.shapePayrollData(data, extras);

    return this.auditWithResponse(activity, shapedData);
  }

  // not automatically subscribing here
  public auditWithResponse(activity: string, data?: object) {
    let headers = new HttpHeaders();
    headers = headers.set(this.errorInterceptorSkipHeader, '');
    headers = headers.set(this.loadingInterceptorSkipHeader, '');

    return this.http.post<any>(
      `${this.env.apiUrlRoot}/Audit`,
      {
        Activity: activity,
        Data: data
      },
      {
        headers
      }
    );
  }

  public getEmployeeAuditData(
    clientCode: string,
    args: {
      personId?: string;
      pageNum?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: string;
      activityQuery?: string;
      attrNameQuery?: string;
      userIdQuery?: string;
      employeeNumberQuery?: string;
      employeeNameQuery?: string;
      startDate?: Date;
      endDate?: Date;
    }
  ): Observable<PagedPayload<EmployeeEditRecord>> {
    let params = new HttpParams();

    args.pageNum = args.pageNum || 1;
    args.pageSize = args.pageSize || 100;
    args.personId = args.personId || '';

    params = params.set('personId', args.personId.trim());
    params = params.set('pageNum', args.pageNum.toString());
    params = params.set('pageSize', args.pageSize.toString());

    // filters
    if (args.activityQuery) {
      params = params.set('activityQuery', args.activityQuery.trim());
    }
    if (args.attrNameQuery) {
      params = params.set('attrNameQuery', args.attrNameQuery.trim());
    }
    if (args.userIdQuery) {
      params = params.set('userIdQuery', args.userIdQuery.trim());
    }
    if (args.employeeNumberQuery) {
      params = params.set('employeeNumberQuery', args.employeeNumberQuery.trim());
    }
    if (args.employeeNameQuery) {
      params = params.set('employeeNameQuery', args.employeeNameQuery.trim());
    }
    if (args.startDate) {
      // if a custom date range is picked,
      // this will be in date picker format {year: '', month: '', day: ''}
      args.startDate = isFormatted(args.startDate)
        ? parseDatePickerValue(args.startDate)
        : args.startDate;
      params = params.set('startDate', new Date(args.startDate).toISOString());
    }
    if (args.endDate) {
      // if a custom date range is picked,
      // this will be in date picker format {year: '', month: '', day: ''}
      args.endDate = isFormatted(args.endDate) ? parseDatePickerValue(args.endDate) : args.endDate;
      params = params.set('endDate', new Date(args.endDate).toISOString());
    }

    // sorting
    if (args.sortKey) {
      params = params.set('sortKey', args.sortKey.trim());
    }
    if (args.sortDirection) {
      params = params.set('sortDirection', args.sortDirection.trim());
    }

    return this.http.get<PagedPayload<EmployeeEditRecord>>(
      `${this.env.apiUrlRoot}/Audit/${clientCode}`,
      {
        params
      }
    );
  }

  public searchPayrollJournal(config: PayrollJournalSearchConfig) {
    return this.http.post<PagedPayload<PayrollJournalEntry>>(
      `${this.env.apiUrlRoot}/Audit/SearchPayrollJournal`,
      config
    );
  }

  private shapePayrollData(data: IPayrollEventData, extras?: any): any {
    return {
      ...data,
      ...{
        // convert payroll type enum value to string representation
        PayrollType: PayTypes[data.PayrollType] as string,
        // We store audit data as a Dictionary<string, string> so we need to serialize
        // Calendars since it is a complex object
        Calendars: JSON.stringify(data.Calendars)
      },
      ...extras
    };
  }
}
